import mainCaller from './mainCaller';

// 에러는 여기서 삼키지 않는다. 호출부(try/catch·useQuery·useMutation onError)가 토스트로 알린다.
// 예전엔 .catch(console.log) 로 undefined 를 돌려줘 500 이 나도 모달이 조용히 닫혔다.

// =====================================================
// Golf Area API (골프 구역 관리)
// =====================================================

/**
 * 골프 구역을 생성한다
 * @param {string} token - 인증 토큰
 * @param {Object} data - 구역 생성 데이터
 * @param {string} data.areaName - 구역 이름
 * @param {string} data.areaMemo - 구역 메모
 * @param {number} data.columnCount - 가로 칸 수
 * @param {number} data.rowCount - 세로 칸 수
 * @param {number} data.branchId - 지점 ID
 * @returns {Promise} 생성 결과
 */
export async function createGolfArea(token, data) {
  return await mainCaller('admin/api/v1/golf/area/create', 'POST', data, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 지점 ID를 사용하여 구역 목록을 가져온다
 * @param {string} token - 인증 토큰
 * @param {number} branchId - 지점 ID
 * @returns {Promise<Array>} 구역 목록
 */
export async function getAreaListByBranchId(token, branchId) {
  return await mainCaller(`admin/api/v1/golf/area/${branchId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 구역 ID를 사용하여 구역 정보를 변경한다
 * @param {string} token - 인증 토큰
 * @param {number} id - 골프 구역 ID
 * @param {Object} data - 수정할 구역 데이터
 * @returns {Promise} 수정 결과
 */
export async function updateGolfAreaById(token, id, data) {
  return await mainCaller(`admin/api/v1/golf/area/${id}`, 'PATCH', data, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 구역 ID를 사용하여 골프 구역을 삭제한다
 * @param {string} token - 인증 토큰
 * @param {number} id - 골프 구역 ID
 * @returns {Promise} 삭제 결과
 */
export async function deleteGolfAreaById(token, id) {
  return await mainCaller(`admin/api/v1/golf/area/${id}`, 'DELETE', null, {
    Authorization: `Bearer ${token}`,
  });
}

// =====================================================
// Golf Area Spot API (골프 구역 스팟 관리)
// =====================================================

/**
 * 골프 스팟 ID를 사용하여 스팟 정보를 수정한다
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @param {Object} data - 수정할 스팟 데이터
 * @returns {Promise} 수정 결과
 */
export async function updateGolfAreaSpotById(token, spotId, data) {
  return await mainCaller(`admin/api/v1/golf/area/spot/${spotId}`, 'PATCH', data, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 스팟 ID를 사용하여 스팟 상세 정보를 가져온다
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @returns {Promise<Object>} 스팟 상세 정보
 */
export async function getGolfAreaSpotDetailById(token, spotId) {
  return await mainCaller(`admin/api/v1/golf/area/spot/${spotId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 구역 ID를 사용하여 해당 구역의 모든 스팟을 가져온다
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @returns {Promise<Array>} 스팟 목록
 */
export async function getGolfSpotsByAreaId(token, areaId) {
  return await mainCaller(`admin/api/v1/golf/area/spots/${areaId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// =====================================================
// Golf Spot Setting API (골프 스팟 설정 관리)
// =====================================================

/**
 * 골프 스팟 설정을 생성한다 (기본값으로 생성)
 * @param {string} token - 인증 토큰
 * @param {number} branchId - 지점 ID
 * @returns {Promise} 생성 결과
 */
export async function createGolfSpotSetting(token, branchId) {
  return await mainCaller(
    'admin/api/v1/golf/area/spot/setting',
    'POST',
    { branchId },
    {
      Authorization: `Bearer ${token}`,
    },
  );
}

/**
 * 골프 스팟 설정 ID를 사용하여 설정을 수정한다
 * @param {string} token - 인증 토큰
 * @param {number} settingId - 골프 스팟 설정 ID
 * @param {Object} data - 수정할 설정 데이터
 * @returns {Promise} 수정 결과
 */
export async function updateGolfSpotSetting(token, settingId, data) {
  return await mainCaller(`admin/api/v1/golf/area/spot/setting/${settingId}`, 'PATCH', data, {
    Authorization: `Bearer ${token}`,
  });
}

// =====================================================
// Golf Spot Reservation API (골프 스팟 예약 관리)
// =====================================================

/**
 * 골프 스팟을 예약한다
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @param {Object} data - 예약 데이터
 * @returns {Promise} 예약 결과
 */
export async function reserveGolfSpot(token, spotId, data) {
  return await mainCaller(`admin/api/v1/golf/area/spot/${spotId}/reservation`, 'POST', data, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 스팟 예약 상태를 변경한다
 * @param {string} token - 인증 토큰
 * @param {Object} data - 예약 상태 변경 데이터
 * @returns {Promise} 상태 변경 결과
 */
export async function updateGolfSpotReservationStatus(token, data) {
  return await mainCaller('admin/api/v1/golf/area/spot/reservation/status', 'PATCH', data, {
    Authorization: `Bearer ${token}`,
  });
}

// =====================================================
// Golf Spot Waiting API (골프 스팟 대기자 관리)
// =====================================================

/**
 * 골프 스팟 대기자를 등록한다
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @param {Object} data - 대기자 데이터
 * @returns {Promise} 등록 결과
 */
export async function createSpotWaiting(token, spotId, data) {
  return await mainCaller(`admin/api/v1/golf/area/spot/${spotId}/waiting`, 'POST', data, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 스팟 ID를 사용하여 대기자 정보를 가져온다
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @returns {Promise<Object>} 대기자 정보
 */
export async function getSpotWaitingInfo(token, spotId) {
  return await mainCaller(`admin/api/v1/golf/area/spot/${spotId}/waiting`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 구역 id를 사용하여 구역의 전체 대기자 목록을 가져온다.
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @returns {Promise<Array>} 대기자 목록
 */
export async function getSpotWaitingInfoAll(token, spotId) {
  return await mainCaller(`admin/api/v1/golf/area/waitings/${spotId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 구역 id를 사용하여 구역의 전체 대기자 목록을 가져온다.
 * @param {string} token - 인증 토큰
 * @param {number} id - 골프 스팟 ID
 * @returns {Promise} 스팟 이용 내역
 */
export async function getGolfSpotHistory(token, id) {
  return await mainCaller(`admin/api/v1/golf/area/spot/${id}/usage-history`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 스팟 ID를 사용하여 모든 대기자 목록을 가져온다
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @returns {Promise<Array>} 대기자 목록
 */
export async function getSpotWaitingAll(token, spotId) {
  return await mainCaller(`admin/api/v1/golf/area/spot/${spotId}/waiting/all`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 스팟 대기자를 삭제한다
 * @param {string} token - 인증 토큰
 * @param {number} spotId - 골프 스팟 ID
 * @param {Object} data - 삭제할 대기자 데이터
 * @returns {Promise} 삭제 결과
 */
export async function deleteSpotWaiting(token, spotId, data) {
  return await mainCaller(`admin/api/v1/golf/area/spot/${spotId}/waiting`, 'DELETE', data, {
    Authorization: `Bearer ${token}`,
  });
}

// =====================================================
// Golf Settings API (골프 설정 관리)
// =====================================================

/**
 * 지점 ID를 사용하여 골프 스팟 설정 정보를 가져온다
 * @param {string} token - 인증 토큰
 * @param {number} branchId - 지점 ID
 * @returns {Promise<Object>} 골프 스팟 설정 정보
 */
export async function getGolfSettingsByBranchId(token, branchId) {
  return await mainCaller(`admin/api/v1/golf/settings/${branchId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 지점 ID를 사용하여 골프 스팟 기본 설정 정보를 가져온다
 * @param {string} token - 인증 토큰
 * @param {number} golfAreaId - 구역 ID
 * @returns {Promise<Object>} 골프 스팟 설정 정보
 */
export async function getGolfDefaultSettingsByAreaId(token, golfAreaId) {
  return await mainCaller(`admin/api/v1/golf/area/spot/default-setting/${golfAreaId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 지점 ID를 사용하여 골프 스팟 기본 설정 정보를 수정한다.
 * @param {string} token - 인증 토큰
 * @param {number} golfAreaId - 골프 스팟 ID
 * @param {Object} data - 기본 설정 데이터
 * @returns {Promise} 등록 결과
 */

export async function updateGolfDefaultSettingsByAreaId(token, golfAreaId, data) {
  return await mainCaller(`admin/api/v1/golf/area/spot/default-setting/${golfAreaId}`, 'POST', data, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 스팟 이동
 * @param {string} token - 인증 토큰
 * @param {Object} data - 예약 상태 변경 데이터
 * @returns {Promise} 상태 변경 결과
 */
export async function updateGolfSpotReservationSpotMove(token, data) {
  return await mainCaller('admin/api/v1/golf/area/spot/reservation/move', 'PATCH', data, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * 골프 스팟 예약 시간 연장
 * @param {string} token - 인증 토큰
 * @param {Object} data - 스팟 이동 데이터
 * @returns {Promise} 상태 변경 결과
 */
export async function updateGolfSpotReservationExtendTime(token, data) {
  return await mainCaller('admin/api/v1/golf/area/spot/reservation/extend', 'PATCH', data, {
    Authorization: `Bearer ${token}`,
  });
}

export async function cancelGolfSpotReservation(token, reservationId) {
  return await mainCaller(
    `admin/api/v1/golf/area/spot/reservation/close`,
    'PATCH',
    { reservationId },
    {
      Authorization: `Bearer ${token}`,
    },
  );
}

export async function updateGolfReservation(token, data) {
  return await mainCaller(`admin/api/v1/golf/area/spot/reservation/update`, 'PATCH', data, {
    Authorization: `Bearer ${token}`,
  });
}

export async function getKioskSettings(token, branchId) {
  return await mainCaller(`admin/api/v1/golf/kiosk-settings/${branchId}`, 'GET', null, {
    Authorization: `Bearer ${token}`,
  });
}

// 골프 키오스크 설정
export async function updateKioskSettings(token, data) {
  // FormData인 경우와 일반 JSON 데이터 모두 mainCaller 사용
  // mainCaller가 FormData를 자동으로 처리함
  return await mainCaller('admin/api/v1/golf/kiosk-settings', 'PUT', data, {
    Authorization: `Bearer ${token}`,
    ...(data instanceof FormData && { 'Content-Type': 'multipart/form-data' }),
  });
}

/**
 * 골프 회원앱 예약 설정을 수정한다
 * @param {string} token - 인증 토큰
 * @param {Object} data - 설정 데이터
 * @param {number} data.branchId - 지점 ID
 * @param {string} data.reservationEnabled - 예약 가능 여부 ('Y' | 'N')
 * @param {string} data.advanceReservationEnabled - 사전 예약 활성화 ('Y' | 'N')
 * @param {number} data.advanceReservationDays - 사전 예약 가능 일수
 * @param {string} data.advanceTimeSlotOption - 사전 예약 시간대 옵션 ('BRANCH_TIMES' | 'CUSTOM')
 * @param {number} data.advanceTimeUnitMinutes - 사전 예약 시간 단위 (분)
 * @param {number} data.advanceUnavailableTimes - 사전 예약 불가능 시간 (시)
 * @param {string} data.realtimeReservationEnabled - 실시간 예약 활성화 ('Y' | 'N')
 * @param {string} data.realtimeTimeSlotOption - 실시간 예약 시간대 옵션 ('BRANCH_TIMES' | 'CUSTOM')
 * @param {string} data.shortTimeReservationEnabled - 숏타임 예약 활성화 ('Y' | 'N')
 * @param {string} data.shortTimeYn - 숏타임 예약 가능 여부 ('Y' | 'N')
 * @returns {Promise<Object>} 수정된 설정 정보
 */
export async function updateGolfSettings(token, data) {
  return await mainCaller('admin/api/v1/golf/settings', 'PUT', data, {
    Authorization: `Bearer ${token}`,
  });
}
